import { useEffect, useRef } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { useTheme } from "@/hooks/useTheme";

const WAVE_COUNT = 8;

export function SlideWaveBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const reducedMotion = useReducedMotion();
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let time = 0;

    const isDark = theme === "dark";
    const strokeRgb = isDark ? "255, 255, 255" : "0, 0, 0";

    const resize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (!rect) return;
      width = rect.width;
      height = rect.height;
      const dpr = window.devicePixelRatio;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      for (let w = 0; w < WAVE_COUNT; w++) {
        const wFrac = w / WAVE_COUNT;
        const baseY = height * (0.1 + wFrac * 0.8);
        const amplitude = 20 + w * 6;
        const frequency = 0.004 + wFrac * 0.002;
        const speed = 0.0002 + wFrac * 0.00008;
        const phase = time * speed + w * 0.9;

        const alpha = 0.03 + (1 - wFrac) * 0.05;
        ctx.strokeStyle = `rgba(${strokeRgb}, ${alpha})`;
        ctx.lineWidth = 1;

        ctx.beginPath();
        for (let x = 0; x <= width; x += 2) {
          const y =
            baseY +
            Math.cos(x * frequency + phase) * amplitude +
            Math.cos(x * frequency * 0.5 + phase * 1.3) * amplitude * 0.3;
          if (x === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        }
        ctx.stroke();
      }

      if (!reducedMotion) {
        time += 16;
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion, theme]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute pointer-events-none"
      style={{ top: "-32px", left: "-40px", right: "-40px", bottom: "-32px", width: "calc(100% + 80px)", height: "calc(100% + 64px)" }}
      aria-hidden="true"
    />
  );
}
